import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEventActions } from 'src/store/event/actions';
import { RootState } from 'src/store/reducers/state';
import { useNavigate, useParams } from 'react-router-dom';
import { getEventType, minutesToHourMinutes } from 'src/components/utilities/utils';
import moment from 'moment';
interface SummaryRow {
    label: string;
    value: string;
}

interface SummarySection {
    id: number;
    title: string;
    rows: SummaryRow[];
}


const checklist = [
    { label: 'Content selected', complete: true },
    { label: 'Event details complete', complete: true },
    { label: 'Registration form configured', complete: true },
    { label: 'Event page branding set', complete: true },
    { label: 'Confirmation email enabled', complete: true }
];


const SummaryCard: React.FC<{ section: SummarySection; onEdit: (id: number) => void }> = ({ section, onEdit }) => (
    <div className="border border-gray-100 rounded-lg">
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-indigo-50 rounded-t-lg">
            <h3 className="text-sm font-semibold text-gray-900 m-0 min-w-0">{section.title}</h3>
            <button
                type="button"
                onClick={() => onEdit(section.id)}
                className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:underline py-1 pl-2">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Edit
            </button>
        </div>
        <dl className="px-4 py-3 space-y-2">
            {section.rows.map((row) => (
                <div key={row.label} className="flex flex-col md:flex-row md:items-start gap-0.5 md:gap-3 min-w-0">
                    <dt className="text-xs font-semibold text-gray-500 md:w-44 flex-shrink-0">{row.label}</dt>
                    <dd className="text-sm text-gray-800 m-0 min-w-0 break-words">{row.value}</dd>
                </div>
            ))}
        </dl>
    </div>
);

const ReviewPublish = ({ setCurrentStep, channelName }: any) => {
    const dispatch = useDispatch()
    const eventAction = useEventActions(dispatch)
    const navigate = useNavigate()
    const { eventId } = useParams()
    const [agreedToPublish, setAgreedToPublish] = useState(false);
    const [summarySectionData, setSummarySectionData] = useState([
        {
            id: 1,
            title: 'Event Setup',
            rows: [{ label: 'Content Type', value: '' }]
        },
        {
            id: 2,
            title: 'Select or Create Content',
            rows: [
                // { label: 'Content', value: 'The Latest AI Strategies for Surveyors' },
                { label: 'Channel', value: '' }
            ]
        },
        {
            id: 3,
            title: 'Event Details',
            rows: [
                { label: 'Event Name', value: '' },
                { label: 'Date & Time', value: '' },
                { label: 'Duration', value: '' },
                // { label: 'Access Level', value: 'Library User (recommended)' }
            ]
        },
        {
            id: 4,
            title: 'Registration Setup',
            rows: [
                // { label: 'Registration Form', value: 'Enabled' },
                { label: 'Require Registration', value: '' },
                // { label: 'Required Fields', value: 'Email, First Name, Last Name, Organization, Job Title' }
            ]
        },
        {
            id: 5,
            title: 'Images & Style',
            rows: [
                // { label: 'Logo', value: 'Uploaded (200px width)' },
                // { label: 'Banner Image', value: 'Uploaded' },
                { label: 'Header Background Color', value: '#7C3AED' }
            ]
        },
        {
            id: 6,
            title: 'Email & Notifications',
            rows: [
                // { label: 'Confirmation Email', value: 'Enabled' },
                { label: 'ICS Attachment', value: '' },
                // { label: 'Google Calendar Integration', value: 'Disabled' }
            ]
        }
    ])
    const {
        singleEvent,
        isEventUpdated,
        eventRegistrationImagesView
    } = useSelector((state: RootState) => {
        return {
            singleEvent: state.event.singleEvent,
            isEventUpdated: state.event.isEventUpdated,
            eventRegistrationImagesView: state.event.singleEvent.eventRegistrationField
                ? state.event.singleEvent.eventRegistrationField.eventRegistrationImagesViewModel
                : null,
        };
    });
    const handleEdit = (stepId: number) => {
        setCurrentStep(stepId)
    };
    const completeSetup = () => {
        navigate('/eventOverview', { state: { eventId, eventName: singleEvent?.name } })
    }
    const getRequireRegistration = (value: boolean) => {
        if (value == true) {
            return 'Yes, before accessing the event'
        }
        return 'No';
    }
    const getSendICSAttachment = (value: boolean | undefined) => {
        if (value == true) {
            return 'Enabled'
        }
        return 'Disabled';
    }

    useEffect(() => {
        if (singleEvent) {
            summarySectionData && summarySectionData.map((item) => {
                if (item.id === 1) {
                    summarySectionData[0].rows[0].value = getEventType(singleEvent.eventType)
                }
                if (item.id === 2) {
                    summarySectionData[1].rows[0].value = channelName
                }
                if (item.id === 3) {
                    summarySectionData[2].rows[0].value = singleEvent.name
                    summarySectionData[2].rows[1].value = `${singleEvent.displayDate} ${singleEvent.displayTime}`
                    summarySectionData[2].rows[2].value =
                        singleEvent.eventInDays
                            ? `${moment
                                .duration(singleEvent.duration || 0, 'minutes')
                                .days()} Days`
                            : minutesToHourMinutes(singleEvent.duration);
                }
                if (item.id === 4) {
                    summarySectionData[3].rows[0].value = getRequireRegistration(singleEvent.memberRegistrationRequired)
                }
                if (item.id === 5) {
                    summarySectionData[4].rows[0].value = eventRegistrationImagesView?.headerBackgroundColor
                }
                if (item.id === 6) {
                    summarySectionData[5].rows[0].value = getSendICSAttachment(eventRegistrationImagesView?.sendICSAttachment)
                }
            })
            setSummarySectionData(summarySectionData)
        }
    }, [singleEvent, channelName])

    return (


        <main className="flex-1 flex flex-col min-w-0">
            <div className="bg-white rounded-xl border border-gray-200 flex-1 flex flex-col">
                <div className="px-4 sm:px-8 pt-5 sm:pt-6 pb-4 border-b border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-900 m-0">Review &amp; Publish</h2>
                    <p className="text-sm text-gray-400 mt-1">{`Review your event settings below, then publish when you're ready to go live.`}</p>
                </div>

                <div className="px-4 sm:px-8 py-5 sm:py-6 space-y-6">
                    {/* Pre-publish checklist */}
                    <div className="bg-green-50 rounded-lg p-4">
                        <p className="text-sm font-semibold text-gray-900 m-0 mb-2">Pre-publish checklist</p>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-6 gap-y-1.5">
                            {checklist.map((item) => (
                                <div key={item.label} className="flex items-center gap-2 text-sm text-gray-700">
                                    <span className="flex items-center justify-center w-4 h-4 rounded-full bg-green-500 text-white flex-shrink-0">
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                    </span>
                                    {item.label}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Summary sections */}
                    <div className="space-y-4">
                        {summarySectionData.map((section) => (
                            <SummaryCard key={section.id} section={section} onEdit={handleEdit} />
                        ))}
                    </div>

                    {/* Publish confirmation */}
                    {/* <div className="border border-gray-100 rounded-lg p-4 flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    checked={agreedToPublish}
                                    onChange={(e) => setAgreedToPublish(e.target.checked)}
                                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-400 flex-shrink-0"
                                />
                                <div>
                                    <p className="m-0 text-sm font-semibold text-gray-700">{`I've reviewed the event details above`}</p>
                                    <p className="m-0 text-xs text-gray-400 mt-0.5">
                                        Once published, this event will be live on your event page and the registration form will become
                                        active. You can still edit most settings after publishing.
                                    </p>
                                </div>
                            </div> */}

                </div>

                <div className="flex-1" />

                {/* Footer */}
                <div className="flex justify-between gap-3 px-4 sm:px-8 py-4 sm:py-5 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={() => setCurrentStep(6)}
                        className="flex-shrink-0 flex items-center gap-2 text-sm font-semibold text-gray-600 border border-gray-200 rounded-lg px-4 sm:px-5 py-2.5 hover:bg-gray-50">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <line x1="19" y1="12" x2="5" y2="12" />
                            <polyline points="12 19 5 12 12 5" />
                        </svg>
                        Back
                    </button>
                    <button
                        type="button"
                        // disabled={!agreedToPublish}
                        onClick={() => completeSetup()}
                        className="flex-1 sm:flex-none justify-center flex items-center gap-2 text-sm font-semibold rounded-lg px-4 sm:px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white">
                        Complete Setup
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M22 2L11 13" />
                            <path d="M22 2l-7 20-4-9-9-4 20-7z" />
                        </svg>
                    </button>
                </div>
            </div>
        </main>

    );
};

export default ReviewPublish;